import { PaymentMethod } from 'types';
import type {
  ApplePayToken,
  MakePaymentV3Params,
} from 'services/repositories/payment/makePaymentV3';
import { generateUniqueId } from './generateUniqueId';

type PaymentMethodPayload = MakePaymentV3Params['paymentMethods'][number];

interface BuildApplePayPaymentMethodParams {
  /** FULL PKPaymentToken (event.payment.token) from onpaymentauthorized. */
  token: ApplePayToken;
  /** Amount to charge, in cents. */
  amountInCents: number;
  /** Installments (captured before the button). Default 1 (one-off). */
  installments?: number;
}

/**
 * Builds the Apple Pay payment item for `POST /v3/payment`.
 *
 * The token comes from the `onpaymentauthorized` event (not from formik), so
 * the item is built here — not inside `buildPaymentMethods`, which reads from
 * the form. BFF contract: the agnostic wallet pair
 * `walletToken: { wallet, token }`, the same one Google Pay uses (FIN-7487) —
 * a new wallet needs no new field anywhere in the chain. What stays
 * Apple-specific is the token SHAPE: the FULL `PKPaymentToken` object, whose
 * `paymentData` the backend decrypts and whose `paymentMethod.network` feeds the
 * acquirer mapping.
 */
export function buildApplePayPaymentMethod({
  token,
  amountInCents,
  installments = 1,
}: BuildApplePayPaymentMethodParams): PaymentMethodPayload {
  return {
    virtualId: generateUniqueId(),
    type: PaymentMethod.APPLE_PAY,
    amount: amountInCents,
    installments,
    walletToken: { wallet: 'APPLE_PAY', token },
  };
}
